import React, { useRef } from 'react';
import { Button, Typography } from 'antd';
import './KeyPad.scss';

const { Text } = Typography;

export interface KeyPadProps {
  onKeyHandle: (value: string) => void;
}

export interface IKeyValue {
  main: string;
  addition: string[];
}

export function KeyPad({ onKeyHandle }: KeyPadProps) {
  const audioCtxRef = useRef<AudioContext | null>(null);

  const ensureAudioContext = (): AudioContext => {
    if (!audioCtxRef.current) {
      const Ctx =
        (window as any).AudioContext || (window as any).webkitAudioContext;
      audioCtxRef.current = new Ctx();
    }
    return audioCtxRef.current;
  };

  const dtmfMap: Record<string, [number, number]> = {
    '1': [697, 1209],
    '2': [697, 1336],
    '3': [697, 1477],
    '4': [770, 1209],
    '5': [770, 1336],
    '6': [770, 1477],
    '7': [852, 1209],
    '8': [852, 1336],
    '9': [852, 1477],
    '*': [941, 1209],
    '0': [941, 1336],
    '#': [941, 1477],
  };

  const playDtmf = (key: string, durationMs = 120) => {
    const ctx = ensureAudioContext();
    const freqs = dtmfMap[key];
    if (!freqs) return;

    if (ctx.state === 'suspended') {
      ctx.resume();
    }

    const gain = ctx.createGain();
    gain.gain.value = 0.05;
    gain.connect(ctx.destination);

    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    osc1.type = 'sine';
    osc2.type = 'sine';
    osc1.frequency.value = freqs[0];
    osc2.frequency.value = freqs[1];
    osc1.connect(gain);
    osc2.connect(gain);
    const now = ctx.currentTime;
    const end = now + durationMs / 1000;
    osc1.start(now);
    osc2.start(now);
    gain.gain.setValueAtTime(0.0, now);
    gain.gain.linearRampToValueAtTime(0.06, now + 0.01);
    gain.gain.setValueAtTime(0.06, end - 0.02);
    gain.gain.linearRampToValueAtTime(0.0, end);
    osc1.stop(end);
    osc2.stop(end);
    osc1.onended = () => {
      osc1.disconnect();
      osc2.disconnect();
      gain.disconnect();
    };
  };
  const handlePressStart = (e: any) => {
    const target = e.currentTarget as HTMLDivElement;
    target.classList.add('is-pressed');
  };

  const handlePressEnd = (e: any) => {
    const target = e.currentTarget as HTMLDivElement;
    target.classList.remove('is-pressed');
  };

  const numbers: IKeyValue[][] = [
    [
      { main: '1', addition: [] },
      { main: '2', addition: ['A', 'B', 'C'] },
      { main: '3', addition: ['D', 'E', 'F'] },
    ],
    [
      { main: '4', addition: ['G', 'H', 'I'] },
      { main: '5', addition: ['J', 'K', 'L'] },
      { main: '6', addition: ['M', 'N', 'O'] },
    ],
    [
      { main: '7', addition: ['P', 'Q', 'R', 'S'] },
      { main: '8', addition: ['T', 'U', 'V'] },
      { main: '9', addition: ['W', 'X', 'Y', 'Z'] },
    ],
    [
      { main: '*', addition: [] },
      { main: '0', addition: ['+'] },
      { main: '#', addition: [] },
    ],
  ];

  const clickOnKey = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;

    if (
      target.classList.contains('key-pad__row') ||
      target.classList.contains('key-pad')
    ) {
      return;
    }

    const cell = target.closest('.key-pad__cell');

    if (!cell) {
      return;
    }

    const keyElement = cell.querySelector('.main-key');

    if (!keyElement) {
      return;
    }

    const keyNumber = keyElement.textContent;

    playDtmf(keyNumber);
    onKeyHandle(keyNumber);
    e.preventDefault();
  };

  const renderKeyPad = () => {
    const rows = numbers.map((row, i) => {
      const keys = row.map((key) => {
        const additionText = key.addition.join('');

        return (
          <div
            className="key-pad__cell"
            key={key.main}
            onMouseDown={handlePressStart}
            onMouseUp={handlePressEnd}
            onMouseLeave={handlePressEnd}
            onTouchStart={handlePressStart}
            onTouchEnd={handlePressEnd}
          >
            <Button className="main-key" type="text">
              {key.main}
            </Button>
            <Text className="additional-keys" type="secondary" strong>
              {additionText}
            </Text>
          </div>
        );
      });

      const renderKeys = keys.reduce((prev, curr) => [prev, curr] as any);

      return (
        <div key={i} className="key-pad__row">
          {renderKeys}
        </div>
      );
    });

    return (
      <div className="key-pad" onClick={clickOnKey}>
        {rows}
      </div>
    );
  };

  return renderKeyPad();
}
